import { Box, BoxProps } from '@squareup/dex-ui-shared-base';
import { isNull } from '@squareup/dex-utils-shared-json';
import clsx from 'clsx';
import React, {
  FC,
  PropsWithChildren,
  memo,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';

import { ApiExplorerFieldLabel } from './ApiExplorerFieldLabel';
import styles from './api-explorer-field.module.css';
import { CloseButton } from './helpers/ApiExplorerCloseButton';
import { ApiExplorerDepthWrapper } from './helpers/ApiExplorerDepthWrapper';
import { ExpandButton } from './helpers/ApiExplorerExpandButton';
import { ApiExplorerFieldNullState } from './helpers/ApiExplorerFieldNullState';
import helperStyles from './helpers/api-explorer-depth-wrapper.module.css';
import { ApiExplorerCollapsibleFieldProps } from './props';

type Props = PropsWithChildren<ApiExplorerCollapsibleFieldProps>;

const ApiExplorerCollapsibleFieldLabel: FC<
  Props & Pick<BoxProps, 'margin' | 'padding'>
> = memo(
  ({
    currentValue,
    depth = 0,
    onChange,
    path,
    name,
    property,
    typeField,
    children,
    margin,
    padding,
  }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const onCloseButtonClicked = useCallback(() => {
      onChange(undefined, path, 'remove');
      setIsCollapsed(false);
    }, [path, onChange]);

    const onCollapseClicked = useCallback(() => {
      setIsCollapsed(true);
    }, []);

    const onExpandClicked = useCallback(() => {
      setIsCollapsed(false);
    }, []);

    const labelName = name || property?.name || '';

    // Only show the close button if the object exists
    const showCloseButton = typeof currentValue !== 'undefined';
    const closeButton = showCloseButton ? (
      <CloseButton
        labelName={labelName}
        onClick={onCloseButtonClicked}
        className={styles['object-close-button']}
      />
    ) : null;

    const expandButton = isCollapsed ? (
      <ExpandButton labelName={labelName} onClick={onExpandClicked} />
    ) : null;

    const collapsibleDepthWrapperId = useId();

    const isEmptyState =
      currentValue === undefined ||
      (Array.isArray(currentValue) && currentValue.length === 0);
    const isNullState = isNull(currentValue);

    const state = useMemo(() => {
      if (isNullState) {
        return <ApiExplorerFieldNullState onRemove={onCloseButtonClicked} />;
      } else if (isEmptyState) {
        return children;
      } else {
        return (
          <ApiExplorerDepthWrapper
            id={collapsibleDepthWrapperId}
            depth={depth}
            onClick={onCollapseClicked}
          >
            {children}
          </ApiExplorerDepthWrapper>
        );
      }
    }, [
      children,
      collapsibleDepthWrapperId,
      depth,
      isEmptyState,
      isNullState,
      onCloseButtonClicked,
      onCollapseClicked,
    ]);

    return (
      <Box
        testId={`api-explorer-collapsible-field-${labelName}`}
        className={clsx(isCollapsed && helperStyles['collapsed'])}
        margin={margin}
        padding={padding}
        aria-controls={collapsibleDepthWrapperId}
      >
        <ApiExplorerFieldLabel
          property={property}
          name={name}
          typeField={typeField}
          preChildren={expandButton}
          postChildren={closeButton}
        />
        {state}
      </Box>
    );
  }
);

ApiExplorerCollapsibleFieldLabel.displayName = 'ApiExplorerCollapsibleField';

export { ApiExplorerCollapsibleFieldLabel };
